import React from "react";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./index.css";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";

import { Home, Intro, Week, Tracker } from "./pages/Course";
import Privacy from "./pages/Privacy";
import Settings from "./pages/Settings";
import Sponsors from "./pages/Sponsors";
import { Toaster } from "./components/ui/toaster";
// EmergentAffiliate banner kept in codebase for future use, but not rendered now
// import EmergentAffiliate from "./components/EmergentAffiliate";
import DiaryCalendar from "./pages/DiaryCalendar";
import DiaryDay from "./pages/DiaryDay";
import DiaryNew from "./pages/DiaryNew";
import DiaryEdit from "./pages/DiaryEdit";
import "./lib/timewarp"; // side-effect import to register hidden debug API
import AdSlots from "./components/AdSlots";
import AdOverlay from "./components/AdOverlay";
import AffiliateDisclosure from "./pages/AffiliateDisclosure";
import Contact from "./pages/Contact";
import Forum from "./pages/Forum";
import ForumCreatePost from "./pages/ForumCreatePost";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminContactList from "./pages/admin/AdminContactList";
import AdminContactThread from "./pages/admin/AdminContactThread";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminCounters from "./pages/admin/AdminCounters";
import AdminAds from "./pages/admin/AdminAds";
import AdminAdCreate from "./pages/admin/AdminAdCreate";
import AdminForum from "./pages/admin/AdminForum";
import AdminGuideEditor from "./pages/admin/AdminGuideEditor";
import { AdminRouteGuard } from "./components/admin/AdminRouteGuard";
import Refer from "./pages/Refer";
import { maybeTrackRefOnLocation } from "./lib/referrals";
import AnalyticsConsentBanner from "./components/AnalyticsConsent";
import { FORUM_ENABLED } from "./lib/features";

function RefTracker() {
  const location = useLocation();
  React.useEffect(() => {
    maybeTrackRefOnLocation(location.search);
  }, [location.search]);
  return null;
}

function App() {
  const [hideEmergentBanner, setHideEmergentBanner] = React.useState(false);

  React.useEffect(() => {
    const onAds = (e) => {
      const { emergentShowing } = (e && e.detail) || { emergentShowing: false };
      setHideEmergentBanner((prev) => prev || !!emergentShowing);
    };
    window.addEventListener("rl-ads-status", onAds);
    return () => window.removeEventListener("rl-ads-status", onAds);
  }, []);

  return (
    <div data-tour-id="site-root" className="min-h-screen bg-background text-foreground flex flex-col">
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="rl_theme_v1">
        <BrowserRouter>
          {/* Referral tracker watching query params */}
          <RefTracker />

          {/* Analytics consent modal (global + blocking until a choice is made) */}
          <AnalyticsConsentBanner />

          {/* Global Ad Overlay */}
          <AdOverlay />

          <NavBar />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/intro" element={<Intro />} />
              <Route path="/week/:weekNumber" element={<Week />} />
              <Route path="/tracker" element={<Tracker />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/sponsors" element={<Sponsors />} />
              <Route path="/diary" element={<DiaryCalendar />} />
              <Route path="/diary/day" element={<DiaryDay />} />
              <Route path="/diary/new" element={<DiaryNew />} />
              <Route path="/diary/edit" element={<DiaryEdit />} />
              <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/forum" element={FORUM_ENABLED ? <Forum /> : <Navigate to="/" replace />} />
              <Route path="/forum/create" element={FORUM_ENABLED ? <ForumCreatePost /> : <Navigate to="/" replace />} />
              <Route path="/refer" element={<Refer />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/counters" element={<AdminRouteGuard><AdminCounters /></AdminRouteGuard>} />
              <Route path="/admin/ads" element={<AdminRouteGuard><AdminAds /></AdminRouteGuard>} />
              <Route path="/admin/ads/new" element={<AdminRouteGuard><AdminAdCreate /></AdminRouteGuard>} />
              <Route path="/admin/guide" element={<AdminRouteGuard><AdminGuideEditor /></AdminRouteGuard>} />

              <Route path="/admin/contact" element={<AdminRouteGuard><AdminContactList /></AdminRouteGuard>} />
              <Route path="/admin/contact/thread/:threadId" element={<AdminRouteGuard><AdminContactThread /></AdminRouteGuard>} />
              <Route path="/admin/referrals" element={<AdminRouteGuard><AdminReferrals /></AdminRouteGuard>} />
              <Route path="/admin/forum" element={<AdminRouteGuard><AdminForum /></AdminRouteGuard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          {/* New: global ad slots (above the affiliate banner) */}
          <AdSlots />

          {/* Affiliate banner temporarily disabled by request; component retained but not rendered */}
          {/* <EmergentAffiliate /> */}

          <Footer />
        </BrowserRouter>
      </ThemeProvider>
      <Toaster />
    </div>
  );
}

export default App;